import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Clock, ShieldAlert, Loader2, Users, Download, Search, ChevronDown } from 'lucide-react';
import { formatDisplayDate } from '@/utils/dateFormat';

// A leader's standing page: no sign-in, the token in the address is the key. Built for a phone
// held in one hand inside WhatsApp's browser - one column, big tap targets, nothing to learn.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const timeOf = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
};

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-muted/30">
      <div className="bg-[#1B3A8C] px-4 py-4">
        <p className="text-white font-heading font-bold tracking-wide">KEYA PULSE</p>
        <p className="text-[11px] text-white/70">Daily reports</p>
      </div>
      <div className="max-w-xl mx-auto px-4 py-5 space-y-4">{children}</div>
    </div>
  );
}

export default function LeaderLinkPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  // A leader with forty FEs has hundreds of pages here, so the team opens one day at a time and
  // can be searched by name or beat.
  const [openDay, setOpenDay] = useState(null);
  const [find, setFind] = useState('');

  useEffect(() => {
    axios.get(`${API}/l/${token}`)
      .then(r => setData(r.data))
      .catch(() => setFailed(true));
  }, [token]);

  if (failed) return (
    <Shell>
      <p className="text-sm text-muted-foreground">This page could not be loaded. Try again in a moment.</p>
    </Shell>
  );

  if (!data) return (
    <Shell>
      <p className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading your reports...
      </p>
    </Shell>
  );

  if (!data.ok) return (
    <Shell>
      <div className="bg-card border border-amber-500/40 rounded-md p-4 flex items-start gap-3">
        <ShieldAlert size={18} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium">{data.message}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Check your email for the current link, or ask the KEYA PULSE admin to send it again.
          </p>
        </div>
      </div>
    </Shell>
  );

  const today = todayISO();
  const days = [];
  (data.reports || []).forEach(r => {
    const group = days.find(g => g.date === r.date);
    if (group) group.items.push(r); else days.push({ date: r.date, items: [r] });
  });
  const expiringSoon = data.expires_in <= (data.renew || 7);

  // Their FEs' own briefings and scorecards, by day and then by person.
  const hunt = find.trim().toLowerCase();
  const teamDays = [];
  (data.team || []).forEach(r => {
    if (hunt && !`${r.fe} ${(r.beats || []).join(' ')}`.toLowerCase().includes(hunt)) return;
    const day = teamDays.find(g => g.date === r.date) || (teamDays.push({ date: r.date, fes: [] }), teamDays[teamDays.length - 1]);
    const fe = day.fes.find(f => f.key === r.fe_key) || (day.fes.push({ key: r.fe_key, name: r.fe, beats: r.beats || [], items: [] }), day.fes[day.fes.length - 1]);
    fe.items.push(r);
  });
  const firstTeamDay = teamDays.length ? teamDays[0].date : null;
  const shownDay = openDay || firstTeamDay;

  return (
    <Shell>
      <div>
        <h1 className="text-xl font-heading font-bold">{data.name}</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Your Summary for Leaders and roll-up, always the latest, and your team&rsquo;s own pages
          underneath. Keep this link to yourself.
        </p>
      </div>

      {days.length === 0 && (
        <div className="bg-card border border-border rounded-md p-4">
          <p className="text-sm font-medium">Nothing here yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            The evening reports appear here as soon as they are built. This page always shows the
            last week.
          </p>
        </div>
      )}

      {days.map(group => (
        <div key={group.date} className="bg-card border border-border rounded-md overflow-hidden">
          <div className="px-4 py-2 bg-muted/60 flex items-baseline justify-between">
            <p className="text-xs font-heading font-semibold uppercase tracking-widest">
              {group.date === today ? 'Today' : formatDisplayDate(group.date)}
            </p>
            {group.date === today && (
              <span className="text-[11px] text-muted-foreground">{formatDisplayDate(group.date)}</span>
            )}
          </div>
          {group.items.map(r => (
            <a key={r.id} href={`${API}/l/${token}/${r.id}`} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-3 px-4 py-3 border-t border-border/50 active:bg-muted/50"
               data-testid="link-report">
              <FileText size={18} className="text-[#1B3A8C] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{r.label}</p>
                <p className="text-[11px] text-muted-foreground">
                  {r.built_at ? `Built ${timeOf(r.built_at)}` : 'Ready'}
                  {r.size ? ` · ${Math.max(1, Math.round(r.size / 1024))} KB` : ''}
                </p>
              </div>
              <span className="text-[11px] text-[#1B3A8C] font-medium shrink-0">Open</span>
            </a>
          ))}
        </div>
      ))}

      {(data.team_size > 0) && (
        <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="link-team">
          <div className="px-4 py-3 border-b border-border/60">
            <p className="text-sm font-heading font-semibold flex items-center gap-2">
              <Users size={15} className="text-[#1B3A8C]" /> Your team
              <span className="text-xs font-normal text-muted-foreground">
                {data.team_size} field executive{data.team_size === 1 ? '' : 's'}
              </span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Each one&rsquo;s own Beat Briefing and Beat Scorecard, for the same week.
            </p>
            {(data.team || []).length > 6 && (
              <div className="relative mt-2">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={find} onChange={e => setFind(e.target.value)} data-testid="team-search"
                  placeholder="Find a name or a beat"
                  className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-border bg-background" />
              </div>
            )}
          </div>

          {teamDays.length === 0 && (
            <p className="px-4 py-3 text-xs text-muted-foreground">
              {hunt ? 'Nobody by that name on your team this week.' : 'Nothing built for your team yet.'}
            </p>
          )}

          {teamDays.map(day => (
            <div key={day.date} className="border-t border-border/50">
              <button onClick={() => setOpenDay(shownDay === day.date ? 'none' : day.date)}
                data-testid={`team-day-${day.date}`}
                className="w-full px-4 py-2.5 flex items-center justify-between bg-muted/40 active:bg-muted/70">
                <span className="text-xs font-heading font-semibold uppercase tracking-widest">
                  {day.date === today ? 'Today' : formatDisplayDate(day.date)}
                  <span className="ml-2 font-normal normal-case tracking-normal text-muted-foreground">
                    {day.fes.length} FE{day.fes.length === 1 ? '' : 's'}
                  </span>
                </span>
                <ChevronDown size={15} className={`text-muted-foreground transition-transform ${shownDay === day.date ? 'rotate-180' : ''}`} />
              </button>

              {shownDay === day.date && (
                <>
                  <a href={`${API}/l/${token}/day/${day.date}`}
                     className="flex items-center gap-2 px-4 py-2 border-t border-border/40 text-[#1B3A8C] active:bg-muted/50"
                     data-testid="team-day-zip">
                    <Download size={14} /><span className="text-xs font-medium">Download the whole day</span>
                  </a>
                  {day.fes.map(fe => (
                    <div key={fe.key} className="px-4 py-2.5 border-t border-border/40">
                      <p className="text-sm font-medium truncate">{fe.name}</p>
                      {fe.beats.length > 0 && (
                        <p className="text-[11px] text-muted-foreground truncate">{fe.beats.join(', ')}</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        {fe.items.map(r => (
                          <a key={r.id} href={`${API}/l/${token}/${r.id}`} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-[#1B3A8C] font-medium" data-testid="team-report">
                            {r.label}
                            {r.size ? <span className="ml-1 font-normal text-muted-foreground">
                              {Math.max(1, Math.round(r.size / 1024))} KB</span> : null}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={`rounded-md border p-3 flex items-start gap-2 ${expiringSoon
        ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-card'}`} data-testid="link-expiry">
        <Clock size={14} className={`mt-0.5 shrink-0 ${expiringSoon ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          This link expires in <b>{data.expires_in} day{data.expires_in === 1 ? '' : 's'}</b>
          {data.expires_on ? ` (${formatDisplayDate(data.expires_on)})` : ''}.
          {expiringSoon
            ? ' A new one has been emailed to you — pin that one in its place.'
            : ' A new one is emailed to you before it does.'}
          {' '}Anyone who has this address can open it, so please don’t forward it outside your team.
          Every time it is opened is recorded.
        </p>
      </div>
    </Shell>
  );
}
